import React from 'react';
import { BarsForm } from './forms/bars-form';
import { ColumnsForm } from './forms/columns-form';
import { DownloadsForm } from './forms/downloads-form';
import { LineForm } from './forms/line-form';
import { MultilineForm } from './forms/multiline-form';
import { NumberForm } from './forms/number-form';
import { PieForm } from './forms/pie-form';
import { RateForm } from './forms/rate-form';
import { RateTrioForm } from './forms/rate-trio-form';
import { SplitForm } from './forms/split-form';
import { TrioForm } from './forms/trio-form';
import type { EnvelopeSourceConfig } from './sources/create-envelope-source';
import type { Granularity } from '~stores/kpi-envelope';
import type { KpiCardData, KpiForm, KpiFormProps } from './types';

/**
 * Whether a ready result has nothing to draw AT ALL, which is not the same
 * thing as rows that are all zero and must not be drawn the same way.
 *
 * A fixed-domain axis (the region ones) always emits its keys, so an empty
 * period still gives it rows and it renders a legend of zeros: measured, and it
 * was zero. An open axis (`category`, `promotion`) emits nothing, and no chart
 * library draws marks for an empty series, so the card comes out blank and reads
 * as broken. This is what earns those cards a note instead.
 *
 * Scalar forms answer `false` on purpose: a zero is a value, not an absence.
 */
export const hasNoRows = (data: KpiCardData): boolean => {
    switch (data.form) {
        case 'pie':
            return data.slices.length === 0;
        case 'bars':
            return data.steps.length === 0;
        case 'columns':
            return data.columns.length === 0;
        case 'line':
            return data.points.length === 0;
        case 'multiline':
            // Nothing drawable AND nothing in the legend: a base of zero every
            // day (a narrow region filter) otherwise reads as an empty plot
            // area under N/A legends, with no note saying why.
            return (
                data.series.length === 0 ||
                data.series.every(
                    (line) =>
                        line.value === null &&
                        line.points.every((point) => point.value === null)
                )
            );
        case 'downloads':
            // A platform's absent day is its own freshness signal, and its
            // period total stands whatever the points hold, so only "not one
            // series answered" is rowless here.
            return data.series.length === 0;
        case 'number':
        case 'rate':
        case 'split':
        case 'trio':
        case 'rate-trio':
            return false;
    }
};

/** Everything true of a form whatever source serves it. */
export interface KpiFormDefinition {
    render: React.FC<KpiFormProps>;
    /** Row granularity a request must ask for to feed this form. */
    granularity: Granularity;
    /** Whether a card of this form takes a compare window. */
    comparable: boolean;
    /** Source-config keys a source must set to serve this form, read by the
     * check that runs when a source is constructed. */
    requires: readonly (keyof EnvelopeSourceConfig)[];
}

/** Total over `KpiForm`: a new form does not compile until it is described here,
 * which is what keeps a renderer lookup, the compare resolver and each request
 * builder from answering for it on their own. */
export const FORM_DEFINITIONS: Record<KpiForm, KpiFormDefinition> = {
    number: {
        render: NumberForm,
        granularity: 'total',
        comparable: true,
        requires: [],
    },
    rate: {
        render: RateForm,
        granularity: 'total',
        comparable: true,
        requires: [],
    },
    line: {
        render: LineForm,
        granularity: 'daily',
        comparable: false,
        requires: [],
    },
    pie: {
        render: PieForm,
        granularity: 'total',
        comparable: false,
        requires: [],
    },
    bars: {
        render: BarsForm,
        granularity: 'total',
        comparable: false,
        requires: [],
    },
    columns: {
        render: ColumnsForm,
        granularity: 'total',
        comparable: false,
        requires: [],
    },
    split: {
        render: SplitForm,
        granularity: 'total',
        comparable: true,
        requires: ['split'],
    },
    downloads: {
        render: DownloadsForm,
        granularity: 'daily',
        comparable: false,
        requires: [],
    },
    multiline: {
        render: MultilineForm,
        granularity: 'daily',
        comparable: false,
        requires: [],
    },
    trio: {
        render: TrioForm,
        granularity: 'total',
        comparable: false,
        requires: [],
    },
    'rate-trio': {
        render: RateTrioForm,
        granularity: 'total',
        comparable: false,
        requires: [],
    },
};
